import { useState } from 'react';
import type { Asset, AssetKind } from '@avs/contract';
import { apiClient } from '../../api/client.js';
import { useApiCall } from '../../hooks/useApi.js';
import { showToast } from '../../hooks/useToasts.js';
import { uploadOneFile } from '../../utils/uploadFlow.js';
import { Icon } from '../../icons/Icon.js';
import { Button } from '../Button.js';
import { SegmentedControl } from '../SegmentedControl.js';
import { EmptyState } from '../EmptyState.js';
import type { WizardPair } from './types.js';

const ASSET_TABS = [
  { key: 'video', label: '视频',   icon: 'video' as const },
  { key: 'image', label: '图片',   icon: 'image' as const },
  { key: 'group', label: '图片组', icon: 'folder' as const },
] as const;

let pairCounter = 0;
const newPairId = () => `pair-${++pairCounter}-${Math.random().toString(36).slice(2, 6)}`;

type Props = {
  pairs: WizardPair[];
  onChange: (pairs: WizardPair[]) => void;
};

export function StepPairing({ pairs, onChange }: Props) {
  const [tab, setTab] = useState<AssetKind>('video');
  const { data, loading, refresh } = useApiCall(
    () => apiClient.listAssets({ kind: tab, limit: 50 }),
    [tab],
  );
  const assets = data?.items ?? [];

  // 名称缓存：把 assetId 映射到 asset（矩阵展示用），跨 tab 累积
  const [assetMap, setAssetMap] = useState<Record<string, Asset>>({});
  if (assets.length) {
    let dirty = false;
    const next = { ...assetMap };
    for (const a of assets) if (!next[a.id]) { next[a.id] = a; dirty = true; }
    if (dirty) setAssetMap(next);
  }

  async function upload(kind: 'video' | 'image') {
    const inp = document.createElement('input');
    inp.type = 'file'; inp.multiple = true;
    inp.accept = kind === 'video' ? 'video/*' : 'image/*';
    inp.onchange = async () => {
      const files = Array.from(inp.files ?? []);
      if (!files.length) return;
      let ok = 0;
      for (const f of files) { const r = await uploadOneFile(f, kind); if (r.ok) ok++; }
      if (ok) { showToast(`已上传 ${ok} 个`, 'ok'); if (tab !== kind) setTab(kind); else refresh(); }
    };
    inp.click();
  }

  // 点视频 → 加一行配对
  function addVideoRow(videoId: string) {
    onChange([...pairs, { id: newPairId(), videoAssetId: videoId, refAssetIds: [] }]);
  }
  // 点图片/图片组 → 加到「当前选中行」（没有选中行则提示）
  const [activeRow, setActiveRow] = useState<string | null>(null);
  function addRefToActive(refId: string) {
    const target = activeRow ?? pairs[pairs.length - 1]?.id ?? null;
    if (!target) { showToast('请先从「视频」tab 选一个视频加一行', 'info'); return; }
    onChange(pairs.map((p) => {
      if (p.id !== target) return p;
      if (p.refAssetIds.includes(refId)) return { ...p, refAssetIds: p.refAssetIds.filter((x) => x !== refId) };
      return { ...p, refAssetIds: [...p.refAssetIds, refId] };
    }));
  }
  function removeRow(id: string) {
    onChange(pairs.filter((p) => p.id !== id));
    if (activeRow === id) setActiveRow(null);
  }

  const onVideoTab = tab === 'video';

  return (
    <div className="wiz-pairing">
      {/* 左：素材库 */}
      <div className="wiz-panel">
        <div className="wiz-panel-head">
          <div className="block-title">素材库</div>
          <div className="row">
            <Button size="sm" leftIcon={<Icon name="uploadVideo" size={14} />} onClick={() => void upload('video')}>入库视频</Button>
            <Button size="sm" leftIcon={<Icon name="addImage" size={14} />} onClick={() => void upload('image')}>入库图片</Button>
          </div>
        </div>
        <div className="muted small" style={{ marginBottom: 'var(--space-xs)' }}>
          {onVideoTab ? '点视频卡片 → 在右侧新增一行配对' : '点图片/图片组 → 加到右侧当前行（再点取消）'}
        </div>
        <SegmentedControl items={ASSET_TABS} value={tab} onChange={setTab} />
        <div className="wiz-asset-scroll">
          {loading && assets.length === 0 ? (
            <EmptyState icon="refresh" title="加载中" variant="plain" />
          ) : assets.length === 0 ? (
            <EmptyState icon="folder" title="暂无素材" sub="点上方「入库」上传。" variant="plain" />
          ) : (
            <div className="wiz-asset-grid">
              {assets.map((a) => {
                const used = !onVideoTab && (activeRow ?? pairs[pairs.length - 1]?.id)
                  && pairs.find((p) => p.id === (activeRow ?? pairs[pairs.length - 1]?.id))?.refAssetIds.includes(a.id);
                return (
                  <div
                    key={a.id}
                    className={'wiz-asset-card' + (used ? ' picked' : '')}
                    role="button" tabIndex={0}
                    onClick={() => onVideoTab ? addVideoRow(a.id) : addRefToActive(a.id)}
                  >
                    <div className="wiz-asset-thumb">
                      {a.previewUrl && tab === 'image' ? (
                        <img className="wiz-asset-media" src={a.previewUrl} alt={a.name} loading="lazy" />
                      ) : a.previewUrl && tab === 'video' ? (
                        <video className="wiz-asset-media" src={`${a.previewUrl}#t=0.3`} muted preload="metadata" playsInline />
                      ) : (
                        <Icon name={tab === 'video' ? 'video' : tab === 'group' ? 'folder' : 'image'} size={24} />
                      )}
                      {used && <span className="wiz-asset-check"><Icon name="check" size={12} /></span>}
                    </div>
                    <div className="wiz-asset-name" title={a.name}>{a.name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 右：配对矩阵 */}
      <div className="wiz-panel">
        <div className="block-title">配对矩阵</div>
        <div className="muted small" style={{ marginBottom: 'var(--space-s)' }}>
          视频可重复成多行；每行可选择多张图片。点行选中后，去左侧「图片」tab 点选参考素材。
        </div>
        {pairs.length === 0 ? (
          <EmptyState icon="add" title="还没有配对" sub="从左侧「视频」tab 点一个视频，新增一行配对。" variant="plain" />
        ) : (
          <table className="table wiz-matrix">
            <thead>
              <tr><th>视频</th><th>参考素材（可多选）</th><th style={{ width: 60 }}>操作</th></tr>
            </thead>
            <tbody>
              {pairs.map((p) => {
                const v = assetMap[p.videoAssetId];
                const active = (activeRow ?? pairs[pairs.length - 1]?.id) === p.id;
                return (
                  <tr key={p.id} className={active ? 'wiz-row-active' : ''} onClick={() => setActiveRow(p.id)}>
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <Icon name="video" size={16} />
                        <span className="cell-title" title={v?.name}>{v?.name ?? p.videoAssetId}</span>
                      </div>
                    </td>
                    <td>
                      {p.refAssetIds.length === 0 ? (
                        <span className="muted small">选中本行 → 左侧点图片添加</span>
                      ) : (
                        <div className="wiz-ref-chips">
                          {p.refAssetIds.map((rid) => (
                            <span key={rid} className="wiz-ref-chip">
                              <Icon name="image" size={12} />
                              {assetMap[rid]?.name ?? rid}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <button type="button" className="icon-btn" title="删除行"
                        onClick={(e) => { e.stopPropagation(); removeRow(p.id); }}>
                        <Icon name="trash" size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
